import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import JSZip from 'jszip';
import { uploadStudentFiles } from '../../../services/fileCabinetService';
import Alert from '../../common/Alert';

const FILE_TYPES = [
  { value: 'IEP', label: 'IEP' },
  { value: 'evaluation', label: 'Evaluation' },
  { value: 'progress_report', label: 'Progress Report' },
  { value: 'accommodation', label: 'Accommodation' },
  { value: 'other', label: 'Other' },
];

const MAX_FILE_SIZE = 1024 * 1024 * 1024;
const ACCEPT_TYPES = '*/*';
const KNOWN_EXT = new Set([
  'pdf', 'docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt', 'odt', 'ods', 'odp', 'rtf', 'txt', 'csv',
  'jpg', 'jpeg', 'png', 'tif', 'tiff', 'gif', 'bmp', 'webp', 'zip',
]);

function formatFileSize(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function declaredExtension(name) {
  const match = /\.([a-z0-9]+)$/i.exec(name || '');
  if (!match) return '';
  const ext = match[1].toLowerCase();
  return KNOWN_EXT.has(ext) ? ext : '';
}

function fileExtension(name, kind) {
  if (kind === 'zip') return 'ZIP';
  if (kind === 'pdf') return 'PDF';
  const ext = declaredExtension(name);
  return (ext || 'FILE').toUpperCase();
}

function isZipKind(kind, file) {
  if (kind === 'zip') return true;
  const name = (file?.name || '').toLowerCase();
  return name.endsWith('.zip') || file?.type === 'application/zip' || file?.type === 'application/x-zip-compressed';
}

async function sniffKind(file) {
  const buf = await file.slice(0, 16).arrayBuffer();
  const b = new Uint8Array(buf);
  const at = (i) => b[i] ?? 0;
  if (at(0) === 0x25 && at(1) === 0x50 && at(2) === 0x44 && at(3) === 0x46) return 'pdf';
  if (at(0) === 0x50 && at(1) === 0x4B) return 'zip';
  if (at(0) === 0xFF && at(1) === 0xD8 && at(2) === 0xFF) return 'jpg';
  if (at(0) === 0x89 && at(1) === 0x50 && at(2) === 0x4E && at(3) === 0x47) return 'png';
  if ((at(0) === 0x49 && at(1) === 0x49 && at(2) === 0x2A) || (at(0) === 0x4D && at(1) === 0x4D && at(2) === 0x00 && at(3) === 0x2A)) {
    return 'tiff';
  }
  if (at(0) === 0xD0 && at(1) === 0xCF && at(2) === 0x11 && at(3) === 0xE0) return 'doc';
  return '';
}

async function classifyFile(file) {
  const ext = declaredExtension(file.name);
  if (ext === 'zip' || file.type === 'application/zip' || file.type === 'application/x-zip-compressed') {
    return 'zip';
  }
  if (ext) return ext === 'jpeg' ? 'jpg' : ext;
  const sniffed = await sniffKind(file);
  if (sniffed === 'zip') {
    try {
      const zip = await JSZip.loadAsync(file);
      const names = Object.keys(zip.files);
      if (names.some((n) => n.startsWith('word/') || n.startsWith('xl/') || n.startsWith('ppt/'))) {
        return 'docx';
      }
      return 'zip';
    } catch {
      return 'zip';
    }
  }
  return sniffed || '';
}

function basename(path) {
  const parts = String(path || '').replace(/\\/g, '/').split('/');
  return parts[parts.length - 1] || path;
}

const SKIP_FILE_NAMES = new Set(['.ds_store', 'thumbs.db', 'desktop.ini']);

/** Path shown in the selected-files tree. Folder picks set webkitRelativePath; drops set _relativePath. */
function fileRelativePath(file) {
  const raw = file?._relativePath || file?.webkitRelativePath || file?.name || '';
  return String(raw).replace(/\\/g, '/').replace(/^\/+/, '');
}

function fileKey(file) {
  return fileRelativePath(file) || file?.name || '';
}

function shouldSkipRelativePath(rel) {
  const parts = String(rel || '').split('/').filter(Boolean);
  if (!parts.length) return true;
  if (parts.some((part) => part === '__MACOSX' || part.startsWith('.'))) return true;
  return SKIP_FILE_NAMES.has(parts[parts.length - 1].toLowerCase());
}

function readAllDirectoryEntries(reader) {
  return new Promise((resolve, reject) => {
    const acc = [];
    const readBatch = () => {
      reader.readEntries((batch) => {
        if (!batch.length) {
          resolve(acc);
          return;
        }
        acc.push(...batch);
        readBatch();
      }, reject);
    };
    readBatch();
  });
}

function stampRelativePath(file, rel) {
  try {
    Object.defineProperty(file, '_relativePath', { value: rel, enumerable: false });
  } catch {
    file._relativePath = rel;
  }
  return file;
}

/** Walk a dropped file or folder. webkitRelativePath is empty on drag-and-drop. */
async function collectEntryFiles(entry, prefix = '') {
  const rel = prefix ? `${prefix}/${entry.name}` : entry.name;
  if (entry.isFile) {
    const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
    return [stampRelativePath(file, rel)];
  }
  if (!entry.isDirectory) return [];
  const children = await readAllDirectoryEntries(entry.createReader());
  const nested = [];
  for (const child of children) {
    nested.push(...await collectEntryFiles(child, rel));
  }
  return nested;
}

async function filesFromDataTransfer(dataTransfer) {
  const items = dataTransfer?.items ? Array.from(dataTransfer.items) : [];
  const entries = items
    .map((item) => (typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null))
    .filter(Boolean);
  const hadDirectory = entries.some((entry) => entry.isDirectory);
  if (hadDirectory) {
    const collected = [];
    for (const entry of entries) {
      collected.push(...await collectEntryFiles(entry));
    }
    return { files: collected, hadDirectory: true };
  }
  return { files: Array.from(dataTransfer?.files || []), hadDirectory: false };
}

function buildFolderTree(files) {
  const root = { folders: new Map(), files: [] };
  files.forEach((file) => {
    const parts = fileRelativePath(file).split('/').filter(Boolean);
    const inner = parts.slice(1);
    let node = root;
    inner.slice(0, -1).forEach((segment) => {
      if (!node.folders.has(segment)) {
        node.folders.set(segment, { folders: new Map(), files: [] });
      }
      node = node.folders.get(segment);
    });
    node.files.push(file);
  });
  const sortNode = (node) => {
    node.files.sort((a, b) => basename(fileRelativePath(a)).localeCompare(basename(fileRelativePath(b))));
    [...node.folders.keys()].sort((a, b) => a.localeCompare(b)).forEach((name) => {
      const child = node.folders.get(name);
      node.folders.delete(name);
      node.folders.set(name, child);
      sortNode(child);
    });
  };
  sortNode(root);
  return root;
}

function countTreeFolders(node) {
  let count = 0;
  node.folders.forEach((child) => {
    count += 1 + countTreeFolders(child);
  });
  return count;
}

// Files the server converts to PDF on upload (bundle_extractor._CONVERTIBLE_EXTS).
const CONVERTIBLE_EXTS = new Set([
  'docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt', 'odt', 'ods', 'odp', 'rtf',
  'jpg', 'jpeg', 'png', 'tif', 'tiff', 'gif', 'bmp', 'webp',
]);

/**
 * The documents inside a ZIP (nested folders OK), as the server expands them: PDFs (a name
 * without an extension too - the server checks its content) and Word / Excel / PowerPoint /
 * image files it converts to PDF (``convert``). Any other file is ``skipped``.
 */
export async function listZipEntries(file) {
  const zip = await JSZip.loadAsync(file);
  const entries = [];
  const skipped = [];
  zip.forEach((relativePath, entry) => {
    if (entry.dir) return;
    if (relativePath.startsWith('__MACOSX')) return;
    const name = basename(relativePath);
    if (name.startsWith('.')) return;
    const ext = name.includes('.') ? name.toLowerCase().split('.').pop() : '';
    const convert = CONVERTIBLE_EXTS.has(ext);
    if (ext && ext !== 'pdf' && !convert) {
      skipped.push(relativePath);
      return;
    }
    entries.push({
      path: relativePath,
      name,
      size: entry._data?.uncompressedSize ?? entry.uncompressedSize ?? null,
      convert,
    });
  });
  entries.sort((a, b) => a.path.localeCompare(b.path));
  return { entries, skipped: skipped.sort() };
}

function zipInfoMessage(fileName, preview) {
  if (!preview || preview.loading) {
    return `"${fileName}" is a ZIP — scanning for documents inside…`;
  }
  if (preview.error) {
    return `"${fileName}" could not be opened as a ZIP (${preview.error}).`;
  }
  const n = preview.entries?.length || 0;
  const skipped = preview.skipped || [];
  const left = skipped.length
    ? ` ${skipped.length} other file${skipped.length === 1 ? '' : 's'} will be left out (not a supported type): `
      + `${skipped.slice(0, 3).map(basename).join(', ')}${skipped.length > 3 ? ', …' : ''}.`
    : '';
  if (n === 0) {
    return `"${fileName}" has no PDF, Word, Excel, PowerPoint or image files inside — it will fail on upload.${left}`;
  }
  const converted = (preview.entries || []).filter((e) => e.convert).length;
  const note = converted
    ? ` (${converted} Word / Excel / other file${converted === 1 ? '' : 's'} converted to PDF)`
    : '';
  return `"${fileName}" contains ${n} document${n === 1 ? '' : 's'}${note} — they will expand into one batch job on upload.${left}`;
}

export default function AddFileCabinet() {
  const { id: studentId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileKinds, setFileKinds] = useState({});
  const [zipPreviews, setZipPreviews] = useState({});
  const [fileType, setFileType] = useState('other');
  const [description, setDescription] = useState('');
  const [complianceFlag, setComplianceFlag] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [infoMessages, setInfoMessages] = useState([]);

  const folderGroups = useMemo(() => {
    const groups = new Map();
    selectedFiles.forEach((file) => {
      const rel = fileRelativePath(file);
      if (!rel.includes('/')) return;
      const rootName = rel.split('/')[0];
      if (!groups.has(rootName)) groups.set(rootName, []);
      groups.get(rootName).push(file);
    });
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [selectedFiles]);

  const looseFiles = selectedFiles.filter((file) => !fileRelativePath(file).includes('/'));

  useEffect(() => {
    const zipNotes = selectedFiles
      .filter((f) => isZipKind(fileKinds[fileKey(f)], f))
      .map((f) => zipInfoMessage(basename(fileRelativePath(f)), zipPreviews[fileKey(f)]));
    const folderNotes = folderGroups.map(([rootName, files]) => {
      const tree = buildFolderTree(files);
      const subfolders = countTreeFolders(tree);
      const folderCount = 1 + subfolders;
      return `"${rootName}" — ${files.length} file${files.length === 1 ? '' : 's'} in ${folderCount} folder${folderCount === 1 ? '' : 's'} will upload as individual files.`;
    });
    setInfoMessages([...folderNotes, ...zipNotes]);
  }, [selectedFiles, zipPreviews, fileKinds, folderGroups]);

  const loadZipPreview = useCallback(async (file) => {
    const key = fileKey(file);
    setZipPreviews((prev) => ({
      ...prev,
      [key]: { loading: true, entries: [], error: null },
    }));
    try {
      const { entries, skipped } = await listZipEntries(file);
      setZipPreviews((prev) => ({
        ...prev,
        [key]: { loading: false, entries, skipped, error: null },
      }));
    } catch (err) {
      setZipPreviews((prev) => ({
        ...prev,
        [key]: {
          loading: false,
          entries: [],
          error: err?.message || 'invalid archive',
        },
      }));
    }
  }, []);

  const addFiles = useCallback(async (fileList) => {
    const filesArray = Array.from(fileList || []);
    const errors = [];
    const valid = [];
    const kinds = {};
    let skippedHidden = 0;
    for (const file of filesArray) {
      const rel = fileRelativePath(file);
      if (shouldSkipRelativePath(rel)) {
        skippedHidden += 1;
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`"${basename(rel)}" exceeds 1024 MB limit (${formatFileSize(file.size)})`);
        continue;
      }
      const kind = await classifyFile(file);
      if (!kind) {
        errors.push(
          `"${rel}" is not a supported type. Use PDF, Word, Excel, PowerPoint, TXT, JPG, PNG, TIFF, a ZIP of those, a folder of those files, or a student packet without an extension.`,
        );
        continue;
      }
      kinds[rel] = kind;
      valid.push(file);
    }
    if (skippedHidden && valid.length === 0 && errors.length === 0) {
      errors.push('That folder has no supported files.');
    }
    setValidationErrors(errors);
    setFileKinds((prev) => ({ ...prev, ...kinds }));
    setSelectedFiles((prev) => {
      const existing = new Set(prev.map((f) => fileKey(f)));
      const deduped = valid.filter((f) => !existing.has(fileKey(f)));
      deduped.forEach((file) => {
        if (isZipKind(kinds[fileKey(file)], file)) loadZipPreview(file);
      });
      return [...prev, ...deduped];
    });
  }, [loadZipPreview]);

  const removeByKeys = (keys) => {
    const drop = new Set(keys);
    setSelectedFiles((prev) => prev.filter((file) => !drop.has(fileKey(file))));
    setZipPreviews((prev) => {
      const copy = { ...prev };
      drop.forEach((key) => { delete copy[key]; });
      return copy;
    });
    setFileKinds((prev) => {
      const copy = { ...prev };
      drop.forEach((key) => { delete copy[key]; });
      return copy;
    });
    setValidationErrors([]);
  };

  const removeFile = (file) => {
    removeByKeys([fileKey(file)]);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    try {
      const { files, hadDirectory } = await filesFromDataTransfer(e.dataTransfer);
      if (files.length) {
        addFiles(files);
        return;
      }
      if (hadDirectory) setValidationErrors(['That folder has no files to upload.']);
    } catch (err) {
      setValidationErrors([err?.message || 'Could not read that folder.']);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setAlert(null);
    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => formData.append('files', file));
      formData.append('file_type', fileType);
      if (description.trim()) formData.append('description', description.trim());
      formData.append('compliance_flag', complianceFlag);

      await uploadStudentFiles(studentId, formData);
      const zipCount = selectedFiles.filter((f) => isZipKind(fileKinds[fileKey(f)], f)).length;
      const folderCount = folderGroups.length;
      const msg = zipCount
        ? `${selectedFiles.length} file(s) uploaded (including ${zipCount} ZIP). Expanding its documents into a batch job…`
        : folderCount
          ? `${selectedFiles.length} file(s) from ${folderCount} folder${folderCount === 1 ? '' : 's'} uploaded.`
          : `${selectedFiles.length} file(s) uploaded successfully.`;
      setAlert({ type: 'success', message: msg });
      const nextPath = complianceFlag
        ? `/students/${studentId}/information`
        : `/students/${studentId}/files`;
      setTimeout(() => navigate(nextPath), 1200);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to upload files. Please try again.';
      setAlert({ type: 'error', message: msg });
    } finally {
      setUploading(false);
    }
  };

  const totalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);
  const previewPdfCount = selectedFiles.reduce((sum, f) => {
    if (!isZipKind(fileKinds[fileKey(f)], f)) return sum;
    const preview = zipPreviews[fileKey(f)];
    return sum + (preview?.entries?.length || 0);
  }, 0);

  const renderRemoveButton = (onClick, title) => (
    <button
      type="button"
      onClick={onClick}
      className="p-1.5 rounded-lg text-gray-400 opacity-70 group-hover:opacity-100 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all flex-shrink-0"
      title={title}
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );

  const renderFileRow = (file) => {
    const key = fileKey(file);
    const zip = isZipKind(fileKinds[key], file);
    const preview = zip ? zipPreviews[key] : null;
    const pdfEntries = preview?.entries || [];
    const badge = fileExtension(file.name, fileKinds[key]);
    const label = basename(key);

    return (
      <div key={key} className="space-y-1.5">
        <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700 group hover:border-indigo-200 dark:hover:border-indigo-500/40 transition-colors">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
            zip ? 'bg-amber-100 dark:bg-amber-600/20' : 'bg-indigo-100 dark:bg-indigo-600/20'
          }`}>
            <span className={`text-[9px] font-bold leading-none ${
              zip ? 'text-amber-700 dark:text-amber-300' : 'text-indigo-600 dark:text-indigo-400'
            }`}>
              {badge.slice(0, 4)}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-900 dark:text-white font-medium truncate" title={key}>{label}</p>
            <p className="text-xs text-gray-400 dark:text-slate-500">
              {formatFileSize(file.size)}
              {zip && preview?.loading ? ' · reading ZIP…' : null}
              {zip && !preview?.loading && !preview?.error
                ? ` · ${pdfEntries.length} document${pdfEntries.length === 1 ? '' : 's'} inside`
                : null}
              {zip && preview?.error ? ' · could not read ZIP' : null}
            </p>
          </div>
          {renderRemoveButton(() => removeFile(file), 'Remove file')}
        </div>

        {zip && preview?.loading && (
          <p className="pl-4 text-xs text-gray-400 dark:text-slate-500">Scanning for documents…</p>
        )}

        {zip && !preview?.loading && pdfEntries.length > 0 && (
          <ul className="ml-3 pl-3 border-l-2 border-amber-200 dark:border-amber-800 space-y-1">
            {pdfEntries.map((entry) => (
              <li
                key={entry.path}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white dark:bg-slate-800/80 border border-gray-100 dark:border-slate-700"
              >
                <span className="text-[9px] font-bold text-indigo-600 dark:text-indigo-400 w-7 flex-shrink-0">
                  {entry.convert ? entry.name.split('.').pop().toUpperCase().slice(0, 4) : 'PDF'}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-800 dark:text-slate-200 truncate" title={entry.path}>
                    {entry.name}
                    {entry.convert ? (
                      <span className="ml-1 text-[10px] text-gray-400 dark:text-slate-500">· converted to PDF</span>
                    ) : null}
                  </p>
                  {entry.size != null && (
                    <p className="text-[10px] text-gray-400 dark:text-slate-500">{formatFileSize(entry.size)}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {zip && !preview?.loading && !preview?.error && pdfEntries.length === 0 && (
          <p className="pl-4 text-xs text-amber-700 dark:text-amber-300">
            No PDF, Word, Excel, PowerPoint or image files found in this ZIP.
          </p>
        )}
      </div>
    );
  };

  const renderFolderBranch = (node) => (
    <div className="ml-3 pl-3 border-l-2 border-indigo-200 dark:border-indigo-800 space-y-1.5">
      {[...node.folders.entries()].map(([name, child]) => (
        <div key={name} className="space-y-1.5">
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-amber-50/70 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/40">
            <span className="text-[9px] font-bold text-amber-700 dark:text-amber-300 w-7 flex-shrink-0">DIR</span>
            <p className="text-xs font-semibold text-gray-800 dark:text-slate-200 truncate" title={name}>{name}</p>
          </div>
          {renderFolderBranch(child)}
        </div>
      ))}
      {node.files.map((file) => renderFileRow(file))}
    </div>
  );

  return (
    <div className="-m-4 sm:-m-6 p-4 sm:p-6 flex flex-col max-md:min-h-0 md:h-[calc(100vh-4rem)] md:max-h-[calc(100vh-4rem)] md:overflow-hidden">
      {alert && (
        <div className="flex-shrink-0 mb-3 mx-auto w-full min-w-0">
          <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
        </div>
      )}

      {/* Header */}
      <div className="flex-shrink-0 flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4 mx-auto w-full min-w-0">
        <Link
          to={`/students/${studentId}/files`}
          className="p-1.5 sm:p-2 rounded-xl text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors flex-shrink-0"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white leading-tight">Upload Files</h1>
          <p className="text-gray-500 dark:text-slate-400 text-xs sm:text-sm mt-0.5 truncate">
            Add documents to the student&apos;s file cabinet
          </p>
        </div>
      </div>

      <form
        onSubmit={handleUpload}
        className="max-md:flex-none md:flex-1 flex flex-col min-h-0 mx-auto w-full min-w-0 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl overflow-hidden shadow-sm"
      >
        <div className="max-md:flex-none md:flex-1 min-h-0 grid grid-cols-1 md:grid-cols-2 overflow-hidden">
          {/* Left — upload zone */}
          <div className="flex flex-col min-h-0 p-4 sm:p-5 border-b md:border-b-0 md:border-r border-gray-200 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-900/30 md:overflow-hidden">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-slate-500 mb-2">
              Add files
            </p>
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={() => setDragOver(false)}
              onClick={() => fileInputRef.current?.click()}
              className={`h-32 sm:h-36 md:flex-1 md:min-h-0 flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-4 sm:p-6 text-center cursor-pointer transition-all ${
                dragOver
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-gray-300 dark:border-slate-600 hover:border-indigo-400 dark:hover:border-indigo-500 hover:bg-white dark:hover:bg-slate-800/80 bg-white dark:bg-slate-800/60'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                className="hidden"
                accept={ACCEPT_TYPES}
              />
              <input
                ref={folderInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                {...{ webkitdirectory: '', directory: '' }}
              />
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 transition-colors ${
                dragOver ? 'bg-indigo-200 dark:bg-indigo-600/40' : 'bg-indigo-100 dark:bg-indigo-600/20'
              }`}>
                <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                </svg>
              </div>
              <p className="text-sm text-gray-900 dark:text-white font-semibold">
                {dragOver ? 'Drop files or a folder' : (
                  <>
                    <span className="hidden sm:inline">Drag & drop files or a folder</span>
                    <span className="sm:hidden">Tap to upload</span>
                  </>
                )}
              </p>
              <p className="text-gray-500 dark:text-slate-400 text-xs mt-0.5">
                or <span className="text-indigo-600 dark:text-indigo-400 font-medium">browse files</span>
                {' · '}
                <button
                  type="button"
                  className="text-indigo-600 dark:text-indigo-400 font-medium hover:underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    folderInputRef.current?.click();
                  }}
                >
                  browse folder
                </button>
              </p>
              <p className="text-gray-400 dark:text-slate-500 text-[10px] mt-2 leading-snug">
                PDF, Word, Excel, PowerPoint, TXT, images, a ZIP of those, or a folder with subfolders · Max 1024 MB
              </p>
            </div>

            {infoMessages.length > 0 && (
              <div className="flex-shrink-0 mt-3 p-3 rounded-xl bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 md:max-h-28 md:overflow-y-auto">
                {infoMessages.map((msg, i) => (
                  <p key={i} className="text-sky-700 dark:text-sky-300 text-xs sm:text-sm flex items-start gap-2">
                    <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    {msg}
                  </p>
                ))}
              </div>
            )}

            {validationErrors.length > 0 && (
              <div className="flex-shrink-0 mt-3 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 md:max-h-24 md:overflow-y-auto">
                {validationErrors.map((err, i) => (
                  <p key={i} className="text-red-600 dark:text-red-400 text-xs sm:text-sm flex items-start gap-2">
                    <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {err}
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* Right — selected files + details */}
          <div className="flex flex-col min-h-0 md:flex-1 md:overflow-hidden">
            <div className="p-4 sm:p-5 pb-3 border-b border-gray-100 dark:border-slate-700 md:flex-1 md:min-h-0 md:flex md:flex-col md:overflow-hidden">
              <div className="flex items-center justify-between gap-2 sm:gap-3 mb-3 flex-shrink-0">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-slate-500">
                  Selected files
                </p>
                {selectedFiles.length > 0 && (
                  <span className="text-[11px] sm:text-xs text-gray-500 dark:text-slate-400 text-right whitespace-nowrap">
                    {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''}
                    {folderGroups.length > 0 ? ` · ${folderGroups.length} folder${folderGroups.length === 1 ? '' : 's'}` : ''}
                    {previewPdfCount > 0 ? ` · ${previewPdfCount} document${previewPdfCount === 1 ? '' : 's'} in ZIP` : ''}
                    {' · '}{formatFileSize(totalSize)}
                  </span>
                )}
              </div>

              {selectedFiles.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-6 sm:py-8 px-4 rounded-xl border border-dashed border-gray-200 dark:border-slate-700 bg-gray-50/80 dark:bg-slate-900/40 text-center md:flex-1">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-slate-800 flex items-center justify-center mb-2">
                    <svg className="w-5 h-5 text-gray-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-gray-600 dark:text-slate-300">No files selected yet</p>
                  <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                    <span className="md:hidden">Use the upload area above</span>
                    <span className="hidden md:inline">Add files from the left panel</span>
                  </p>
                </div>
              ) : (
                <div className="space-y-2 md:flex-1 md:min-h-0 md:overflow-y-auto pr-1 [scrollbar-width:thin]">
                  {folderGroups.map(([rootName, files]) => {
                    const tree = buildFolderTree(files);
                    const folderCount = 1 + countTreeFolders(tree);
                    return (
                      <div key={rootName} className="space-y-1.5">
                        <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800 group">
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-amber-100 dark:bg-amber-600/20">
                            <span className="text-[9px] font-bold leading-none text-amber-700 dark:text-amber-300">DIR</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900 dark:text-white font-medium truncate" title={rootName}>{rootName}</p>
                            <p className="text-xs text-gray-400 dark:text-slate-500">
                              {files.length} file{files.length === 1 ? '' : 's'}
                              {` · ${folderCount} folder${folderCount === 1 ? '' : 's'}`}
                            </p>
                          </div>
                          {renderRemoveButton(() => removeByKeys(files.map(fileKey)), 'Remove folder')}
                        </div>
                        {renderFolderBranch(tree)}
                      </div>
                    );
                  })}
                  {looseFiles.map((file) => renderFileRow(file))}
                </div>
              )}
            </div>

            <div className={`flex-shrink-0 p-4 sm:p-5 pt-3 space-y-3 sm:space-y-4 border-t border-gray-100 dark:border-slate-700 ${selectedFiles.length === 0 ? 'opacity-60' : ''}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                      File Type
                    </label>
                    <select
                      value={fileType}
                      onChange={(e) => setFileType(e.target.value)}
                      disabled={selectedFiles.length === 0}
                      className="w-full px-3 py-2.5 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors disabled:cursor-not-allowed"
                    >
                      {FILE_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                      Compliance
                    </label>
                    <button
                      type="button"
                      onClick={() => setComplianceFlag(!complianceFlag)}
                      disabled={selectedFiles.length === 0}
                      className={`w-full flex items-center justify-center gap-1.5 sm:gap-2 px-3 py-2.5 rounded-xl border text-xs sm:text-sm font-medium transition-colors disabled:cursor-not-allowed ${
                        complianceFlag
                          ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400'
                          : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:border-emerald-300 dark:hover:border-emerald-600'
                      }`}
                    >
                      <svg className={`w-4 h-4 ${complianceFlag ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <span className="truncate">Run compliance</span>
                      {complianceFlag && (
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  disabled={selectedFiles.length === 0}
                  placeholder="Optional description for the uploaded file(s)..."
                  className="w-full px-3 py-2.5 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm transition-colors resize-none disabled:cursor-not-allowed"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex-shrink-0 px-4 sm:px-5 py-3 sm:py-4 border-t border-gray-100 dark:border-slate-700 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 bg-white dark:bg-slate-800">
              <Link
                to={`/students/${studentId}/files`}
                className="w-full sm:w-auto px-4 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 text-sm font-medium transition-colors text-center"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={uploading || selectedFiles.length === 0}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 shadow-lg shadow-indigo-500/20"
              >
                {uploading ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Uploading...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                    </svg>
                    Upload {selectedFiles.length > 0 ? `${selectedFiles.length} File${selectedFiles.length > 1 ? 's' : ''}` : 'Files'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
